import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, IconButton, Stack, Button, Chip, Badge,
    CircularProgress, Alert, useMediaQuery, useTheme,
    Tooltip, ToggleButtonGroup, ToggleButton,
    TextField, InputAdornment, MenuItem, Autocomplete,
    Collapse,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import TuneIcon from '@mui/icons-material/Tune';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VerifiedIcon from '@mui/icons-material/Verified';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptCameraDialog from './ReceiptCameraDialog';
import QuotaBadge from './QuotaBadge';
import RemoveIconButton from './common/RemoveIconButton';
import { useAuth } from '../context/AuthContext';
import { getTodayInTimezone } from '../utils/date';
import { Currency, AnimationTiming } from '../constants/enums';
import { filterAndSortCategories, getCategoryOptionLabel, findMatchingCategory } from '../utils/category';


const font = {
    headline: "'Manrope', sans-serif",
    body: "'Work Sans', sans-serif",
};

const PROMPT_PRESETS = [
    { label: 'Remove IDs & Codes', text: 'Remove meaningless transaction IDs and reference codes' },
    { label: 'Clean Transfer Descriptions', text: "Don't show INTERAC e-Transfer, describe the nature of the transfer" },
    { label: 'Title Case Merchants', text: 'Format merchant names in clean Title Case' },
];

// ─── Dynamic Design Tokens Hook ──────────────────────────────────────────────
function useDesignTokens() {
    const theme = useTheme();
    const C = {
        primary: theme.palette.primary.main,
        primaryCont: theme.palette.primary.light,
        onPrimary: theme.palette.primary.contrastText,
        onPrimaryCont: theme.palette.primary.light,
        surface: theme.palette.background.default,
        surfaceContLow: theme.palette.background.paper,
        surfaceCont: theme.palette.background.default,
        surfaceContHigh: alpha(theme.palette.primary.main, 0.05),
        surfaceContHighest: alpha(theme.palette.primary.main, 0.1),
        surfaceDim: alpha(theme.palette.secondary.main, 0.15),
        onSurface: theme.palette.text.primary,
        onSurfaceVar: theme.palette.text.secondary,
        secondaryCont: theme.palette.secondary.light,
        onSecondaryCont: theme.palette.secondary.main,
        outlineVar: alpha(theme.palette.secondary.main, 0.2),
        tertiary: theme.palette.info.main,
        error: theme.palette.error.main,
    };

    const filledInputSx = {
        '& .MuiFilledInput-root': {
            bgcolor: alpha(theme.palette.primary.main, 0.05),
            borderRadius: '12px',
            overflow: 'hidden',
            height: 56,
            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.08) },
            '&.Mui-focused': {
                bgcolor: '#fff',
                boxShadow: `0 0 0 2px ${C.primary}`
            },
            '&:before, &:after': { display: 'none' },
            display: 'flex',
            alignItems: 'center',
        },
        '& .MuiFilledInput-input': {
            py: 0,
            px: 2,
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            fontSize: '0.95rem',
            color: C.primary,
            fontWeight: 500,
            borderRadius: 'inherit',
        },
        '& input:-webkit-autofill': {
            borderRadius: 'inherit',
        },
        '& .MuiAutocomplete-inputRoot': {
            paddingTop: '0 !important',
            paddingBottom: '0 !important',
        },
        '& .MuiAutocomplete-input': {
            padding: '0 !important',
            height: '100%',
        }
    };

    return { C, filledInputSx, theme };
}


// ─── Field Label ──────────────────────────────────────────────────────────────
function FieldLabel({ children }) {
    const { C } = useDesignTokens();
    return (
        <Typography sx={{
            fontFamily: font.body,
            fontSize: '0.625rem',
            fontWeight: 700,
            letterSpacing: '0.18em',
            textTransform: 'uppercase',
            color: C.onSurfaceVar,
            mb: 0.5,
            display: 'block',
        }}>
            {children}
        </Typography>
    );
}

// ─── Transaction Card ─────────────────────────────────────────────────────────
function TransactionCard({ row, index, isRemoving = false, categories, creditCards = [], accounts = [], onUpdate, onRemove, isParsing, isBatchDuplicate }) {
    const { t } = useTranslation();
    const { C, filledInputSx } = useDesignTokens();
    const isDup = row.is_existing_duplicate || isBatchDuplicate;
    return (
        <Box
            component="article"
            sx={{
                bgcolor: isRemoving ? 'rgba(186,26,26,0.06)' : C.surfaceContLow,
                borderRadius: '0.75rem',
                p: 3,
                border: isRemoving
                    ? `1px solid rgba(186,26,26,0.3)`
                    : (isDup ? `1px solid rgba(186,26,26,0.25)` : `1px solid ${C.outlineVar}1A`),
                boxShadow: isRemoving ? 'none' : '0 1px 4px rgba(28,28,24,0.04)',
                opacity: isRemoving ? 0 : 1,
                transform: isRemoving ? 'scale(0.96)' : 'scale(1)',
                pointerEvents: isRemoving ? 'none' : 'auto',
                transition: `all ${AnimationTiming.RECORD_REMOVE_MS}ms cubic-bezier(0.4, 0, 0.2, 1)`,
                '&:hover': { bgcolor: isRemoving ? 'rgba(186,26,26,0.06)' : C.surfaceCont },
            }}
        >
            {/* Duplicate warning banner */}
            {isDup && (
                <Box sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    mb: 2, px: 1.5, py: 0.75, borderRadius: 1.5,
                    bgcolor: 'rgba(186,26,26,0.06)',
                }}>
                    <WarningAmberIcon sx={{ fontSize: 14, color: C.error }} />
                    <Typography sx={{ fontFamily: font.body, fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: C.error }}>
                        {row.is_existing_duplicate
                            ? t("Possible duplicate (already exists in your transactions)")
                            : t("Possible duplicate (another item in this batch)")}
                    </Typography>
                </Box>
            )}

            {/* Row 1: Description */}
            <Box sx={{ mb: 3 }}>
                <FieldLabel>{t('Description')}</FieldLabel>
                <TextField
                    variant="filled"
                    fullWidth
                    disabled={isParsing}
                    InputProps={{ disableUnderline: true }}
                    value={row.description}
                    onChange={e => onUpdate(index, 'description', e.target.value)}
                    sx={{
                        ...filledInputSx,
                        '& .MuiFilledInput-input': {
                            ...filledInputSx['& .MuiFilledInput-input'],
                            fontFamily: font.headline,
                            fontSize: '1rem',
                            fontWeight: 700,
                        }
                    }}
                />
            </Box>

            {/* Row 2: Date + Currency */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, mb: 3, alignItems: 'flex-end' }}>
                <Box>
                    <FieldLabel>{t('Date')}</FieldLabel>
                    <TextField
                        type="date"
                        variant="filled"
                        fullWidth
                        disabled={isParsing}
                        InputProps={{ disableUnderline: true }}
                        value={row.date}
                        onChange={e => onUpdate(index, 'date', e.target.value)}
                        sx={filledInputSx}
                    />
                </Box>
                <Box>
                    <FieldLabel>{t('Currency')}</FieldLabel>
                    <Autocomplete
                        options={[Currency.CAD, Currency.USD, Currency.CNY]}
                        value={row.currency || Currency.CAD}
                        onChange={(e, newValue) => {
                            if (newValue) onUpdate(index, 'currency', newValue);
                        }}
                        disableClearable
                        autoHighlight
                        disabled={isParsing}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                variant="filled"
                                fullWidth
                                sx={filledInputSx}
                                InputProps={{ ...params.InputProps, disableUnderline: true }}
                            />
                        )}
                    />
                </Box>
            </Box>

            {/* Row 3: Amount + Type */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, mb: 3, alignItems: 'flex-end' }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', height: '100%' }}>
                    <FieldLabel>{row.type === 'Move' ? t('From Amount') : t('Amount')}</FieldLabel>
                    <TextField
                        variant="filled"
                        fullWidth
                        type="number"
                        disabled={isParsing}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start" sx={{ color: C.primary, fontWeight: 700, height: '100%', mt: 0 }}>
                                    {row.currency === 'CNY' ? '¥' : '$'}
                                </InputAdornment>
                            ),
                            disableUnderline: true
                        }}
                        slotProps={{
                            htmlInput: { inputMode: 'decimal' }
                        }}
                        value={row.amount}
                        onChange={e => {
                            const val = e.target.value;
                            onUpdate(index, 'amount', val);
                            if (row.type === 'Move') {
                                const fromAcc = accounts.find(a => String(a.id) === String(row.from_account_id));
                                const toAcc = accounts.find(a => String(a.id) === String(row.to_account_id));
                                if (fromAcc && toAcc && fromAcc.currency === toAcc.currency) {
                                    onUpdate(index, 'to_amount', val);
                                } else {
                                    const parsedFrom = parseFloat(val || '0');
                                    const parsedRate = parseFloat(row.exchange_rate || '1.0');
                                    onUpdate(index, 'to_amount', String((parsedFrom * parsedRate).toFixed(2)));
                                }
                            }
                        }}
                        sx={filledInputSx}
                    />
                </Box>
                <Box>
                    <FieldLabel>{t('Type')}</FieldLabel>
                    <ToggleButtonGroup
                        value={row.type || 'Debit'}
                        exclusive
                        fullWidth
                        onChange={(e, val) => {
                            if (val !== null) {
                                onUpdate(index, 'type', val);
                                if (val === 'Credit') {
                                    onUpdate(index, 'payment_method', 'CASH');
                                    onUpdate(index, 'credit_card_id', '');
                                    onUpdate(index, 'cash_back_amount', '');
                                }
                                if (val === 'Move') {
                                    if (accounts && accounts.length > 0) {
                                        const fromId = row.from_account_id || accounts[0].id;
                                        const toId = row.to_account_id || (accounts.find(a => a.id !== fromId) || accounts[0]).id;
                                        onUpdate(index, 'from_account_id', fromId);
                                        onUpdate(index, 'to_account_id', toId);
                                        const fromAcc = accounts.find(a => a.id === fromId);
                                        const toAcc = accounts.find(a => a.id === toId);
                                        if (fromAcc) {
                                            onUpdate(index, 'currency', fromAcc.currency);
                                        }
                                        if (fromAcc && toAcc && fromAcc.currency === toAcc.currency) {
                                            onUpdate(index, 'exchange_rate', '1.0');
                                            onUpdate(index, 'to_amount', row.amount);
                                        } else {
                                            onUpdate(index, 'exchange_rate', row.exchange_rate || '1.0');
                                            const rate = parseFloat(row.exchange_rate || '1.0');
                                            const amount = parseFloat(row.amount || '0');
                                            onUpdate(index, 'to_amount', String((amount * rate).toFixed(2)));
                                        }
                                    }
                                }
                            }
                        }}
                        disabled={isParsing}
                        sx={{
                            width: '100%',
                            '& .MuiToggleButton-root': {
                                flex: 1,
                                textTransform: 'none',
                                fontFamily: font.body,
                                fontWeight: 600,
                                fontSize: '0.75rem',
                                py: 1,
                                border: `1px solid ${C.outlineVar}4D`,
                            }
                        }}
                    >
                        <ToggleButton value="Debit" color="error">{t('Expense')}</ToggleButton>
                        <ToggleButton value="Credit" color="success">{t('Income')}</ToggleButton>
                        {accounts && accounts.length > 0 && (
                            <ToggleButton value="Move" color="primary">{t('Transfer')}</ToggleButton>
                        )}
                    </ToggleButtonGroup>
                </Box>
            </Box>

            {/* Row 4 & 5 conditional rendering */}
            {row.type !== 'Move' && (
                <>
                    {/* Row 4: Payment Method & Details */}
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 3, mb: 3 }}>
                        <Box>
                            <FieldLabel>{t('Payment Method')}</FieldLabel>
                            <ToggleButtonGroup
                                value={row.payment_method || 'CASH'}
                                exclusive
                                fullWidth
                                onChange={(e, val) => {
                                    if (val !== null) {
                                        onUpdate(index, 'payment_method', val);
                                        if (val !== 'CREDIT_CARD') {
                                            onUpdate(index, 'credit_card_id', '');
                                        }
                                        if (val !== 'CASH_BACK') {
                                            onUpdate(index, 'cash_back_amount', '');
                                        }
                                    }
                                }}
                                disabled={isParsing}
                                sx={{
                                    width: '100%',
                                    '& .MuiToggleButton-root': {
                                        flex: 1,
                                        textTransform: 'none',
                                        fontFamily: font.body,
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        color: C.onSurfaceVar,
                                        py: 1.25,
                                        border: `1px solid ${C.outlineVar}4D`,
                                        '&.Mui-selected': {
                                            bgcolor: C.primary,
                                            color: C.onPrimary,
                                            '&:hover': {
                                                bgcolor: C.primaryCont,
                                            }
                                        }
                                    }
                                }}
                            >
                                <ToggleButton value="CASH">{t('Cash')}</ToggleButton>
                                <ToggleButton value="CREDIT_CARD">{t('Credit Card')}</ToggleButton>
                                <ToggleButton value="CASH_BACK">{t('Cash Back')}</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                        {row.payment_method === 'CREDIT_CARD' && (
                            <Box sx={{ mt: 3 }}>
                                <FieldLabel>{t('Credit Card')}</FieldLabel>
                                <TextField
                                    select
                                    variant="filled"
                                    fullWidth
                                    disabled={isParsing}
                                    SelectProps={{ disableUnderline: true }}
                                    value={row.credit_card_id || ''}
                                    onChange={e => onUpdate(index, 'credit_card_id', e.target.value ? Number(e.target.value) : '')}
                                    sx={filledInputSx}
                                >
                                    <MenuItem value="">{t('Select a Card')}</MenuItem>
                                    {creditCards.map(card => (
                                        <MenuItem key={card.id} value={card.id}>
                                            {card.name} (..{card.last_4_digits})
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Box>
                        )}
                        {row.payment_method === 'CASH_BACK' && (
                            <Box sx={{ mt: 3 }}>
                                <FieldLabel>{t('Cash Back Amount')}</FieldLabel>
                                <TextField
                                    variant="filled"
                                    fullWidth
                                    type="number"
                                    disabled={isParsing}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start" sx={{ color: C.primary, fontWeight: 700, height: '100%', mt: 0 }}>
                                                {row.currency === 'CNY' ? '¥' : '$'}
                                            </InputAdornment>
                                        ),
                                        disableUnderline: true
                                    }}
                                    slotProps={{
                                        htmlInput: { inputMode: 'decimal' }
                                    }}
                                    value={row.cash_back_amount || ''}
                                    onChange={e => onUpdate(index, 'cash_back_amount', e.target.value)}
                                    sx={filledInputSx}
                                />
                            </Box>
                        )}
                    </Box>

                    {/* Row 5: Category Dropdown */}
                    <Box sx={{ mb: 3 }}>
                        <FieldLabel>{t('Category')}</FieldLabel>
                        <Autocomplete
                            options={filterAndSortCategories(categories, null, t)}
                            getOptionLabel={(option) => getCategoryOptionLabel(option, categories, t)}
                            isOptionEqualToValue={(option, value) => option.id === value?.id}
                            value={categories.find(c => c.id === row.category_id) || null}
                            onChange={(event, newValue) => {
                                onUpdate(index, 'category_id', newValue ? newValue.id : '');
                            }}
                            disabled={isParsing}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    variant="filled"
                                    fullWidth
                                    sx={filledInputSx}
                                    InputProps={{ ...params.InputProps, disableUnderline: true }}
                                />
                            )}
                            renderOption={(props, option) => {
                                const { key, ...restProps } = props;
                                const paddingLeft = option.parent_id ? '24px' : '0px';
                                return (
                                    <li key={key || option.id} {...restProps}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: paddingLeft }}>
                                            {option.color && (
                                                <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: option.color }} />
                                            )}
                                            {getCategoryOptionLabel(option, categories, t)}
                                        </Box>
                                    </li>
                                );
                            }}
                        />
                    </Box>
                </>
            )}

            {row.type === 'Move' && (
                <Stack spacing={3} sx={{ mb: 3 }}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                        <Box>
                            <FieldLabel>{t('From Account')}</FieldLabel>
                            <TextField
                                select
                                variant="filled"
                                fullWidth
                                disabled={isParsing}
                                SelectProps={{ disableUnderline: true }}
                                value={row.from_account_id || ''}
                                onChange={e => {
                                    const fromId = e.target.value;
                                    onUpdate(index, 'from_account_id', fromId);
                                    if (fromId && String(fromId) === String(row.to_account_id)) {
                                        onUpdate(index, 'to_account_id', '');
                                    }
                                    const fromAcc = accounts.find(a => String(a.id) === String(fromId));
                                    const toAcc = accounts.find(a => String(a.id) === String(row.to_account_id));
                                    if (fromAcc) {
                                        onUpdate(index, 'currency', fromAcc.currency);
                                    }
                                    if (fromAcc && toAcc) {
                                        if (fromAcc.currency === toAcc.currency) {
                                            onUpdate(index, 'exchange_rate', '1.0');
                                            onUpdate(index, 'to_amount', row.amount);
                                        } else {
                                            const rate = parseFloat(row.exchange_rate || '1.0');
                                            const amount = parseFloat(row.amount || '0');
                                            onUpdate(index, 'to_amount', String((amount * rate).toFixed(2)));
                                        }
                                    }
                                }}
                                sx={filledInputSx}
                            >
                                <MenuItem value="">{t('Select From Account')}</MenuItem>
                                {accounts.map(acc => (
                                    <MenuItem key={acc.id} value={acc.id}>
                                        {t(acc.account_type)} ({acc.currency})
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>
                        <Box>
                            <FieldLabel>{t('To Account')}</FieldLabel>
                            <TextField
                                select
                                variant="filled"
                                fullWidth
                                disabled={isParsing}
                                SelectProps={{ disableUnderline: true }}
                                value={row.to_account_id || ''}
                                onChange={e => {
                                    const toId = e.target.value;
                                    onUpdate(index, 'to_account_id', toId);
                                    const fromAcc = accounts.find(a => String(a.id) === String(row.from_account_id));
                                    const toAcc = accounts.find(a => String(a.id) === String(toId));
                                    if (fromAcc && toAcc) {
                                        if (fromAcc.currency === toAcc.currency) {
                                            onUpdate(index, 'exchange_rate', '1.0');
                                            onUpdate(index, 'to_amount', row.amount);
                                        } else {
                                            const rate = parseFloat(row.exchange_rate || '1.0');
                                            const amount = parseFloat(row.amount || '0');
                                            onUpdate(index, 'to_amount', String((amount * rate).toFixed(2)));
                                        }
                                    }
                                }}
                                sx={filledInputSx}
                            >
                                <MenuItem value="">{t('Select To Account')}</MenuItem>
                                {accounts
                                    .filter(acc => String(acc.id) !== String(row.from_account_id))
                                    .map(acc => (
                                        <MenuItem key={acc.id} value={acc.id}>
                                            {t(acc.account_type)} ({acc.currency})
                                        </MenuItem>
                                    ))}
                            </TextField>
                        </Box>
                    </Box>

                    {(() => {
                        const fromAcc = accounts.find(a => String(a.id) === String(row.from_account_id));
                        const toAcc = accounts.find(a => String(a.id) === String(row.to_account_id));
                        const isCrossCurrency = fromAcc && toAcc && fromAcc.currency !== toAcc.currency;

                        if (!isCrossCurrency) return null;

                        return (
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3, alignItems: 'flex-end' }}>
                                <Box>
                                    <FieldLabel>{t('Exchange Rate')}</FieldLabel>
                                    <TextField
                                        variant="filled"
                                        fullWidth
                                        type="number"
                                        disabled={isParsing}
                                        InputProps={{ disableUnderline: true }}
                                        slotProps={{
                                            htmlInput: { inputMode: 'decimal' }
                                        }}
                                        value={row.exchange_rate || ''}
                                        onChange={e => {
                                            const rateVal = e.target.value;
                                            onUpdate(index, 'exchange_rate', rateVal);
                                            const parsedRate = parseFloat(rateVal || '0');
                                            const parsedFrom = parseFloat(row.amount || '0');
                                            onUpdate(index, 'to_amount', String((parsedFrom * parsedRate).toFixed(2)));
                                        }}
                                        sx={filledInputSx}
                                    />
                                </Box>
                                <Box>
                                    <FieldLabel>{t('To Amount')}</FieldLabel>
                                    <TextField
                                        variant="filled"
                                        fullWidth
                                        type="number"
                                        disabled={isParsing}
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start" sx={{ color: C.primary, fontWeight: 700, height: '100%', mt: 0 }}>
                                                    {toAcc.currency === 'CNY' ? '¥' : '$'}
                                                </InputAdornment>
                                            ),
                                            disableUnderline: true
                                        }}
                                        slotProps={{
                                            htmlInput: { inputMode: 'decimal' }
                                        }}
                                        value={row.to_amount || ''}
                                        onChange={e => {
                                            const toVal = e.target.value;
                                            onUpdate(index, 'to_amount', toVal);
                                            const parsedTo = parseFloat(toVal || '0');
                                            const parsedFrom = parseFloat(row.amount || '0');
                                            if (parsedFrom > 0) {
                                                onUpdate(index, 'exchange_rate', String(parsedTo / parsedFrom));
                                            }
                                        }}
                                        sx={filledInputSx}
                                    />
                                </Box>
                            </Box>
                        );
                    })()}
                </Stack>
            )}

            {/* Footer: Remove */}
            <Box sx={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                pt: 2, borderTop: `1px solid ${C.outlineVar}1A`,
            }}>
                <RemoveIconButton
                    onClick={() => !isParsing && !isRemoving && onRemove(index)}
                    disabled={isParsing || isRemoving}
                    label={t('Remove')}
                    title={t('Remove')}
                />
            </Box>
        </Box>
    );
}

// ─── Thumbnail Item ───────────────────────────────────────────────────────────
function ThumbnailItem({ upload, index, isActive, isRemoving = false, onClick, onDelete }) {
    const { C } = useDesignTokens();
    const preview = upload.previewUrl;
    const status = upload.status;

    return (
        <Box
            onClick={!isRemoving ? onClick : undefined}
            sx={{
                flexShrink: 0,
                width: isRemoving ? 0 : 96,
                aspectRatio: isRemoving ? 'unset' : '3/4',
                opacity: isRemoving ? 0 : 1,
                transform: isRemoving ? 'scale(0.5)' : 'scale(1)',
                pointerEvents: isRemoving ? 'none' : 'auto',
                bgcolor: C.surfaceDim,
                borderRadius: '0.5rem',
                overflow: 'hidden',
                border: isActive ? `2px solid ${C.primary}` : '2px solid transparent',
                position: 'relative',
                cursor: isRemoving ? 'default' : 'pointer',
                boxShadow: isActive ? '0 4px 16px rgba(1,45,29,0.2)' : '0 2px 6px rgba(28,28,24,0.08)',
                transition: `all ${AnimationTiming.RECORD_REMOVE_MS}ms ease`,
            }}
        >
            {/* Delete button top right */}
            {status !== 'uploading' && !isRemoving && (
                <IconButton
                    size="small"
                    onClick={(e) => {
                        e.stopPropagation();
                        onDelete();
                    }}
                    sx={{
                        position: 'absolute', top: 4, right: 4,
                        bgcolor: 'rgba(28, 28, 24, 0.6)',
                        color: '#fff',
                        p: '2px',
                        zIndex: 2,
                        '&:hover': {
                            bgcolor: C.error,
                            color: '#fff',
                        },
                        transition: 'all 0.2s ease',
                    }}
                >
                    <CloseIcon sx={{ fontSize: 12 }} />
                </IconButton>
            )}
            {preview && (
                <Box
                    component="img"
                    src={preview}
                    alt={`Doc ${index + 1}`}
                    sx={{
                        width: '100%', height: '100%', objectFit: 'cover',
                        opacity: status === 'uploading' ? 0.6 : 1,
                        filter: status === 'uploading' ? 'grayscale(1)' : 'none',
                        transition: 'all 0.3s ease',
                    }}
                />
            )}
            {!preview && (
                <Box sx={{ width: '100%', height: '100%', bgcolor: C.surfaceContHigh, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', p: 1 }}>
                    <UploadFileIcon sx={{ fontSize: 24, color: C.primary, mb: 0.5 }} />
                    <Typography sx={{ fontFamily: font.body, fontSize: '0.6rem', fontWeight: 700, color: C.onSurfaceVar, letterSpacing: '0.05em', textTransform: 'uppercase', textAlign: 'center', wordBreak: 'break-all', px: 0.5 }}>
                        {upload.file?.name?.includes('.') ? upload.file.name.substring(upload.file.name.lastIndexOf('.') + 1).toUpperCase() : `Doc ${index + 1}`}
                    </Typography>
                </Box>
            )}

            {status === 'uploading' && (
                <Box sx={{
                    position: 'absolute', inset: 0,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    bgcolor: `${C.primary}33`,
                    backdropFilter: 'blur(2px)',
                }}>
                    <RefreshIcon sx={{ color: '#fff', fontSize: 22, animation: 'spin 1s linear infinite', '@keyframes spin': { from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } } }} />
                    <Typography sx={{ color: '#fff', fontFamily: font.body, fontSize: '0.55rem', fontWeight: 700, mt: 0.75, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                        Analyzing
                    </Typography>
                </Box>
            )}
            {status === 'done' && (
                <Box sx={{ position: 'absolute', bottom: 4, right: 4 }}>
                    <CheckCircleIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                </Box>
            )}
            {status === 'error' && (
                <Box sx={{ position: 'absolute', bottom: 4, right: 4 }}>
                    <ErrorOutlineIcon sx={{ fontSize: 16, color: C.error }} />
                </Box>
            )}

            {/* Doc # label */}
            <Box sx={{
                position: 'absolute', top: 4, left: 4,
                bgcolor: 'rgba(28,28,24,0.6)',
                borderRadius: '4px',
                px: 0.75, py: 0.25,
            }}>
                <Typography sx={{ color: '#fff', fontFamily: font.body, fontSize: '0.5rem', fontWeight: 700 }}>
                    #{index + 1}
                </Typography>
            </Box>
        </Box>
    );
}

// ─── Empty Page Placeholder ──────────────────────────────────────────────────
function EmptyPagePlaceholder({ uploadIdx }) {
    const { C } = useDesignTokens();
    return (
        <Box
            sx={{
                bgcolor: alpha(C.primary, 0.03),
                borderRadius: '0.75rem',
                p: 2.5,
                border: `1px dashed ${C.outlineVar}4D`,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                boxShadow: '0 1px 4px rgba(28,28,24,0.02)',
            }}
        >
            <HelpOutlineIcon sx={{ color: C.onSecondaryCont, fontSize: 18 }} />
            <Typography sx={{ fontFamily: font.body, fontSize: '0.85rem', color: C.onSurfaceVar, fontWeight: 500 }}>
                No transactions detected in Page #{uploadIdx + 1}.
            </Typography>
        </Box>
    );
}

// ─── Main Dialog ──────────────────────────────────────────────────────────────
export default function ScanDocumentDialog({
    open,
    onClose,
    api,
    ledgerId,
    categories,
    creditCards = [],
    onSaved,
    accounts = [],
}) {
    const { t } = useTranslation();
    const { preferredTimezone } = useAuth();
    const { C, theme } = useDesignTokens();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const fileInputRef = useRef();

    const [uploads, setUploads] = useState([]);
    const [isParsing, setIsParsing] = useState(false);
    const [parseError, setParseError] = useState('');
    const [reviewRows, setReviewRows] = useState([]);
    const [removingRowKeys, setRemovingRowKeys] = useState(new Set());
    const [removingUploadIndices, setRemovingUploadIndices] = useState(new Set());
    const [isSaving, setIsSaving] = useState(false);
    const [, setTotalParsed] = useState(0);
    const [previewUpload, setPreviewUpload] = useState(null);
    const [cameraOpen, setCameraOpen] = useState(false);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [customScanPrompt, setCustomScanPrompt] = useState('');
    const [promptInput, setPromptInput] = useState('');
    const [isSavingPrompt, setIsSavingPrompt] = useState(false);

    useEffect(() => {
        if (open && ledgerId && api) {
            api.get(`/ledger/ledgers/${ledgerId}/scan-prompt`)
                .then(res => {
                    const promptVal = res.data?.custom_scan_prompt || '';
                    setCustomScanPrompt(promptVal);
                    setPromptInput(promptVal);
                })
                .catch(err => {
                    console.error('Failed to load custom scan prompt', err);
                });
        }
    }, [open, ledgerId, api]);

    const handleSavePrompt = async () => {
        if (!ledgerId || !api) return;
        setIsSavingPrompt(true);
        try {
            const res = await api.put(`/ledger/ledgers/${ledgerId}/scan-prompt`, {
                custom_scan_prompt: promptInput.trim(),
            });
            const updated = res.data?.custom_scan_prompt || '';
            setCustomScanPrompt(updated);
            setPromptInput(updated);
            setSettingsOpen(false);
        } catch (err) {
            console.error('Failed to save scan prompt', err);
        } finally {
            setIsSavingPrompt(false);
        }
    };

    const resetAll = useCallback(() => {
        setUploads(prev => {
            prev.forEach(u => {
                if (u.previewUrl) URL.revokeObjectURL(u.previewUrl);
            });
            return [];
        });
        setIsParsing(false);
        setParseError('');
        setReviewRows([]);
        setRemovingRowKeys(new Set());
        setRemovingUploadIndices(new Set());
        setIsSaving(false);
        setTotalParsed(0);
        setCameraOpen(false);
        setSettingsOpen(false);
    }, []);

    const handleClose = useCallback(() => {
        resetAll();
        onClose();
    }, [resetAll, onClose]);

    // ── File handling ──────────────────────────────────────────────────────────
    const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.pdf', '.csv', '.xlsx', '.xls', '.ofx', '.qfx', '.qbo', '.qif', '.iif'];

    const addFiles = (files) => {
        Array.from(files).forEach(file => {
            const fileName = file.name || '';
            const ext = fileName.includes('.') ? fileName.substring(fileName.lastIndexOf('.')).toLowerCase() : '';
            const isAllowed = file.type.startsWith('image/') ||
                file.type === 'application/pdf' ||
                ALLOWED_EXTENSIONS.includes(ext);

            if (!isAllowed) return;

            let previewUrl = null;
            if (file.type.startsWith('image/') || file.type === 'application/pdf') {
                try {
                    previewUrl = URL.createObjectURL(file);
                } catch {
                    previewUrl = null;
                }
            }
            setUploads(prev => [...prev, { file, status: 'queued', previewUrl, error: null }]);
        });
    };

    const handleFileChange = (e) => addFiles(e.target.files);

    const handleCameraParsed = useCallback((data, blob) => {
        const file = new File([blob], `camera-scan-${Date.now()}.jpg`, { type: 'image/jpeg' });
        const previewUrl = URL.createObjectURL(file);
        window.dispatchEvent(new Event('quota-updated'));


        setUploads(prev => {
            const uploadIdx = prev.length;

            let rawTxns;
            const isStatement = data.document_type === 'BANK_STATEMENT' ||
                data.document_type === 'CREDIT_CARD_BILL' ||
                (Array.isArray(data.transactions) && data.transactions.length > 0);
            let isEmpty = false;

            if (isStatement) {
                if (!data.transactions || data.transactions.length === 0) {
                    isEmpty = true;
                } else {
                    rawTxns = data.transactions;
                }
            } else {
                const desc = (data.store && data.store !== 'Unknown Store')
                    ? data.store
                    : (data.items?.length > 0 ? data.items[0].description : '');
                if (!desc && !data.total) {
                    isEmpty = true;
                } else {
                    rawTxns = [{
                        date: data.date || getTodayInTimezone(preferredTimezone),
                        description: desc,
                        amount: data.total !== undefined ? Math.abs(Number(data.total)) : 0,
                        type: 'Debit',
                        category: data.category || null,
                        category_id: data.category_id || null,
                        is_existing_duplicate: false,
                    }];
                }
            }

            if (isEmpty) {
                const placeholderRow = {
                    _rowKey: `ph_${uploadIdx}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
                    uploadIdx,
                    isEmptyPlaceholder: true,
                };
                setTimeout(() => {
                    setReviewRows(rows => [...rows, placeholderRow]);
                }, 0);
            } else {
                const newRows = rawTxns.map((tx, txIdx) => {
                    const isTxTransfer = isStatement ? (tx.is_transfer || false) : (data.is_transfer || false);
                    let rowType = tx.type || 'Debit';
                    let from_account_id = '';
                    let to_account_id = '';
                    let exchange_rate = '';
                    let to_amount = '';
                    let currency = tx.currency || data.currency || 'CAD';

                    if (isTxTransfer && accounts && accounts.length > 0) {
                        rowType = 'Move';
                        const from_type = isStatement ? tx.from_account_type : data.from_account_type;
                        const from_curr = isStatement ? tx.from_currency : data.from_currency;
                        const to_type = isStatement ? tx.to_account_type : data.to_account_type;
                        const to_curr = isStatement ? tx.to_currency : data.to_currency;

                        const fromAcc = accounts.find(a =>
                            String(a.account_type).toUpperCase() === String(from_type).toUpperCase() &&
                            String(a.currency).toUpperCase() === String(from_curr).toUpperCase()
                        );
                        const toAcc = accounts.find(a =>
                            String(a.account_type).toUpperCase() === String(to_type).toUpperCase() &&
                            String(a.currency).toUpperCase() === String(to_curr).toUpperCase()
                        );

                        if (fromAcc) {
                            from_account_id = fromAcc.id;
                            currency = fromAcc.currency;
                        }
                        if (toAcc) {
                            to_account_id = toAcc.id;
                        }

                        const rawRate = isStatement ? tx.exchange_rate : data.exchange_rate;
                        if (rawRate !== undefined && rawRate !== null) {
                            exchange_rate = String(rawRate);
                        } else {
                            exchange_rate = '1.0';
                        }

                        const amountVal = tx.amount !== undefined ? Math.abs(Number(tx.amount)) : 0;
                        if (fromAcc && toAcc && fromAcc.currency === toAcc.currency) {
                            to_amount = String(amountVal);
                            exchange_rate = '1.0';
                        } else {
                            to_amount = String((amountVal * parseFloat(exchange_rate)).toFixed(2));
                        }
                    }

                    let categoryId = tx.category_id || null;
                    if (!categoryId && tx.category && rowType !== 'Move') {
                        const cat = findMatchingCategory(tx.category, categories, rowType, t);
                        if (cat) categoryId = cat.id;
                    }
                    return {
                        _rowKey: `row_${uploadIdx}_${txIdx}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
                        uploadIdx,
                        date: tx.date || getTodayInTimezone(preferredTimezone),
                        description: tx.description || '',
                        amount: tx.amount !== undefined ? String(Math.abs(Number(tx.amount))) : '',
                        type: rowType,
                        category_id: categoryId,
                        currency: currency,
                        is_existing_duplicate: tx.is_existing_duplicate || false,
                        source_document_id: data.source_document_id || null,
                        payment_method: tx.credit_card_id ? 'CREDIT_CARD' : (tx.cash_back_entry_id ? 'CASH_BACK' : 'CASH'),
                        credit_card_id: tx.credit_card_id || '',
                        cash_back_amount: tx.cash_back_amount || '',
                        from_account_id,
                        to_account_id,
                        exchange_rate,
                        to_amount
                    };
                });

                setTimeout(() => {
                    setReviewRows(rows => [...rows, ...newRows]);
                }, 0);
            }

            return [...prev, { file, status: 'done', previewUrl, error: null }];
        });
    }, [categories, accounts, preferredTimezone, t]);

    const setUploadStatus = (idx, status, error = null) => {
        setUploads(prev => prev.map((u, i) => i === idx ? { ...u, status, error } : u));
    };

    // ── Parse a single file ────────────────────────────────────────────────────
    const parseFile = useCallback(async (fileEntry, uploadIdx) => {
        setUploadStatus(uploadIdx, 'uploading');
        try {
            const fd = new FormData();
            fd.append('file', fileEntry.file);
            if (customScanPrompt) {
                fd.append('custom_prompt', customScanPrompt);
            }
            const params = new URLSearchParams();
            if (ledgerId) params.append('ledger_id', ledgerId);

            const res = await api.post(
                `/source-documents/parse?${params.toString()}`,
                fd,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            const data = res.data;
            setUploadStatus(uploadIdx, 'done');
            window.dispatchEvent(new Event('quota-updated'));


            // ── Normalise response → review rows ──────────────────────────────
            // Statements return data.transactions[]; receipts/invoices return a
            // single-entry shape (data.store / data.total / data.items).
            let rawTxns;
            const isStatement = data.document_type === 'BANK_STATEMENT' ||
                data.document_type === 'CREDIT_CARD_BILL' ||
                (Array.isArray(data.transactions) && data.transactions.length > 0);
            let isEmpty = false;

            if (isStatement) {
                if (!data.transactions || data.transactions.length === 0) {
                    isEmpty = true;
                } else {
                    rawTxns = data.transactions;
                }
            } else {
                const desc = (data.store && data.store !== 'Unknown Store')
                    ? data.store
                    : (data.items?.length > 0 ? data.items[0].description : '');
                if (!desc && !data.total) {
                    isEmpty = true;
                } else {
                    rawTxns = [{
                        date: data.date || getTodayInTimezone(preferredTimezone),
                        description: desc,
                        amount: data.total !== undefined ? Math.abs(Number(data.total)) : 0,
                        type: 'Debit',
                        category: data.category || null,
                        category_id: data.category_id || null,
                        is_existing_duplicate: false,
                    }];
                }
            }

            if (isEmpty) {
                const placeholderRow = {
                    _rowKey: `ph_${uploadIdx}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
                    uploadIdx,
                    isEmptyPlaceholder: true,
                };
                setReviewRows(prev => [...prev, placeholderRow]);
            } else {
                const newRows = rawTxns.map((tx, txIdx) => {
                    const isTxTransfer = isStatement ? (tx.is_transfer || false) : (data.is_transfer || false);
                    let rowType = tx.type || 'Debit';
                    let from_account_id = '';
                    let to_account_id = '';
                    let exchange_rate = '';
                    let to_amount = '';
                    let currency = tx.currency || data.currency || 'CAD';

                    if (isTxTransfer && accounts && accounts.length > 0) {
                        rowType = 'Move';
                        const from_type = isStatement ? tx.from_account_type : data.from_account_type;
                        const from_curr = isStatement ? tx.from_currency : data.from_currency;
                        const to_type = isStatement ? tx.to_account_type : data.to_account_type;
                        const to_curr = isStatement ? tx.to_currency : data.to_currency;

                        const fromAcc = accounts.find(a =>
                            String(a.account_type).toUpperCase() === String(from_type).toUpperCase() &&
                            String(a.currency).toUpperCase() === String(from_curr).toUpperCase()
                        );
                        const toAcc = accounts.find(a =>
                            String(a.account_type).toUpperCase() === String(to_type).toUpperCase() &&
                            String(a.currency).toUpperCase() === String(to_curr).toUpperCase()
                        );

                        if (fromAcc) {
                            from_account_id = fromAcc.id;
                            currency = fromAcc.currency;
                        }
                        if (toAcc) {
                            to_account_id = toAcc.id;
                        }

                        const rawRate = isStatement ? tx.exchange_rate : data.exchange_rate;
                        if (rawRate !== undefined && rawRate !== null) {
                            exchange_rate = String(rawRate);
                        } else {
                            exchange_rate = '1.0';
                        }

                        const amountVal = tx.amount !== undefined ? Math.abs(Number(tx.amount)) : 0;
                        if (fromAcc && toAcc && fromAcc.currency === toAcc.currency) {
                            to_amount = String(amountVal);
                            exchange_rate = '1.0';
                        } else {
                            to_amount = String((amountVal * parseFloat(exchange_rate)).toFixed(2));
                        }
                    }

                    let categoryId = tx.category_id || null;
                    if (!categoryId && tx.category && rowType !== 'Move') {
                        const cat = findMatchingCategory(tx.category, categories, rowType, t);
                        if (cat) categoryId = cat.id;
                    }
                    return {
                        _rowKey: `row_${uploadIdx}_${txIdx}_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
                        uploadIdx,
                        date: tx.date || getTodayInTimezone(preferredTimezone),
                        description: tx.description || '',
                        amount: tx.amount !== undefined ? String(Math.abs(Number(tx.amount))) : '',
                        type: rowType,
                        category_id: categoryId,
                        currency: currency,
                        is_existing_duplicate: tx.is_existing_duplicate || false,
                        source_document_id: data.source_document_id || null,
                        payment_method: tx.credit_card_id ? 'CREDIT_CARD' : (tx.cash_back_entry_id ? 'CASH_BACK' : 'CASH'),
                        credit_card_id: tx.credit_card_id || '',
                        cash_back_amount: tx.cash_back_amount || '',
                        from_account_id,
                        to_account_id,
                        exchange_rate,
                        to_amount
                    };
                });

                setReviewRows(prev => [...prev, ...newRows]);
                setTotalParsed(prev => prev + newRows.length);
            }

        } catch (err) {
            console.error('Parse failed for', fileEntry.file.name, err);
            setUploadStatus(uploadIdx, 'error', 'Parse failed');
            const msg = err.response?.data?.detail || `Failed to parse "${fileEntry.file.name}". Check the file and try again.`;
            setParseError(msg);
        }

    }, [ledgerId, api, categories, accounts, preferredTimezone, t, customScanPrompt]);

    // ── Trigger parse for newly added queued files ─────────────────────────────
    const parseQueued = useCallback(async () => {
        const queued = uploads.map((u, i) => ({ ...u, idx: i })).filter(u => u.status === 'queued');
        if (queued.length === 0) return;
        setIsParsing(true);
        setParseError('');
        for (const u of queued) {
            await parseFile(u, u.idx);
        }
        setIsParsing(false);
    }, [uploads, parseFile]);

    // Run parse whenever new queued uploads appear
    React.useEffect(() => {
        const hasQueued = uploads.some(u => u.status === 'queued');
        if (hasQueued && !isParsing) {
            parseQueued();
        }
    }, [uploads]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Row edit ──────────────────────────────────────────────────────────────
    const updateRow = (idx, field, value) =>
        setReviewRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));

    const removeRow = (idx) => {
        const targetRow = reviewRows[idx];
        const rowKey = targetRow?._rowKey || String(idx);
        setRemovingRowKeys(prev => new Set([...prev, rowKey]));
        setTimeout(() => {
            setReviewRows(prev => prev.filter((r, i) => (r._rowKey ? r._rowKey !== rowKey : i !== idx)));
            setRemovingRowKeys(prev => {
                const next = new Set(prev);
                next.delete(rowKey);
                return next;
            });
        }, AnimationTiming.RECORD_REMOVE_MS);
    };

    const removeUpload = useCallback((idx) => {
        setRemovingUploadIndices(prev => new Set([...prev, idx]));
        setTimeout(() => {
            setUploads(prev => {
                const upload = prev[idx];
                if (upload) {
                    if (upload.previewUrl) URL.revokeObjectURL(upload.previewUrl);
                    setPreviewUpload(current => (current?.file === upload.file ? null : current));
                }
                return prev.filter((_, i) => i !== idx);
            });

            setReviewRows(prev => prev
                .filter(row => row.uploadIdx !== idx)
                .map(row => {
                    if (row.uploadIdx > idx) {
                        return { ...row, uploadIdx: row.uploadIdx - 1 };
                    }
                    return row;
                })
            );
            setRemovingUploadIndices(prev => {
                const next = new Set(prev);
                next.delete(idx);
                return next;
            });
        }, AnimationTiming.RECORD_REMOVE_MS);
    }, []);

    // ── Save ──────────────────────────────────────────────────────────────────
    const handleSave = useCallback(async () => {
        const activeTxns = reviewRows.filter(r => !r.isEmptyPlaceholder && !removingRowKeys.has(r._rowKey || ''));
        if (activeTxns.length === 0 || isSaving) return;
        setIsSaving(true);
        setParseError('');
        try {
            const entryRows = activeTxns.filter(row => row.type !== 'Move');
            const transferRows = activeTxns.filter(row => row.type === 'Move');

            const entryPayload = entryRows.map(row => {
                const parsed = parseFloat((row.amount || '0').replace(/,/g, ''));
                const finalAmount = row.type === 'Credit' ? -Math.abs(parsed) : Math.abs(parsed);
                return {
                    description: row.description,
                    amount: finalAmount,
                    currency: row.currency || 'CAD',
                    date: row.date ? new Date(row.date).toISOString() : new Date().toISOString(),
                    category_id: row.category_id || null,
                    source_document_id: row.source_document_id || null,
                    credit_card_id: row.payment_method === 'CREDIT_CARD' ? (row.credit_card_id || null) : null,
                    cash_back_amount: row.payment_method === 'CASH_BACK' ? parseFloat((row.cash_back_amount || '').toString().replace(/,/g, '')) : null,
                };
            });

            const savePromises = [];
            if (entryPayload.length > 0) {
                savePromises.push(api.post(`/ledger/ledgers/${ledgerId}/entries/bulk`, entryPayload));
            }

            for (const row of transferRows) {
                const parsedFrom = parseFloat((row.amount || '').toString().replace(/,/g, ''));
                const parsedTo = parseFloat((row.to_amount || row.amount || '').toString().replace(/,/g, ''));
                const parsedRate = parseFloat((row.exchange_rate || '1.0').toString().replace(/,/g, ''));
                const transferPayload = {
                    from_account_id: row.from_account_id ? Number(row.from_account_id) : null,
                    to_account_id: row.to_account_id ? Number(row.to_account_id) : null,
                    from_amount: parsedFrom,
                    to_amount: parsedTo,
                    exchange_rate: parsedRate,
                    date: row.date ? row.date : getTodayInTimezone(preferredTimezone),
                    description: row.description || ''
                };
                savePromises.push(api.post(`/ledger/ledgers/${ledgerId}/account-transfers/`, transferPayload));
            }

            await Promise.all(savePromises);
            onSaved?.();
            handleClose();
        } catch (err) {
            console.error('Save failed:', err);
            setParseError('Failed to save transactions. Please try again.');
        } finally {
            setIsSaving(false);
        }
    }, [reviewRows, removingRowKeys, ledgerId, api, onSaved, handleClose, isSaving, preferredTimezone]);

    const activeTxnsCount = reviewRows.filter(r => !r.isEmptyPlaceholder && !removingRowKeys.has(r._rowKey || '')).length;
    const dupCount = reviewRows.filter(r => !r.isEmptyPlaceholder && !removingRowKeys.has(r._rowKey || '') && r.is_existing_duplicate).length;

    const batchDuplicates = React.useMemo(() => {
        const counts = {};
        const activeRows = reviewRows.map((row, index) => {
            if (row.isEmptyPlaceholder) return null;
            const date = row.date || '';
            const cleanAmount = (row.amount || '').toString().replace(/[^0-9.-]/g, '');
            const parsedAmount = parseFloat(cleanAmount);
            const amount = isNaN(parsedAmount) ? '0.00' : parsedAmount.toFixed(2);
            const type = row.type || 'Debit';
            const key = `${date}|${amount}|${type}`;
            return { index, key };
        }).filter(Boolean);

        activeRows.forEach(item => {
            counts[item.key] = (counts[item.key] || 0) + 1;
        });

        const dupIndices = new Set();
        activeRows.forEach(item => {
            if (counts[item.key] > 1) {
                dupIndices.add(item.index);
            }
        });
        return dupIndices;
    }, [reviewRows]);

    const batchDupCount = batchDuplicates.size;

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{
                    sx: {
                        bgcolor: C.surface,
                        backgroundImage: 'none',
                        ...(isMobile ? { borderRadius: 0 } : { borderRadius: '1.5rem' }),
                        boxShadow: '0 40px 100px rgba(1,45,29,0.15)',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column',
                        maxHeight: isMobile ? '100dvh' : '92vh',
                    },
                }}
            >
                {/* ── Top Navigation ── */}
                <Box sx={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    px: 3, height: 64,
                    bgcolor: C.surface,
                    flexShrink: 0,
                }}>
                    <Typography sx={{ fontFamily: font.headline, fontWeight: 700, fontSize: '1.125rem', letterSpacing: '-0.02em', color: C.primary }}>
                        {t('Scan & Ingest Documents')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Tooltip title={t('AI Prompt Settings')}>
                            <IconButton
                                size="small"
                                onClick={() => {
                                    setPromptInput(customScanPrompt);
                                    setSettingsOpen(true);
                                }}
                                sx={{
                                    color: customScanPrompt ? C.primary : C.onSurfaceVar,
                                    '&:hover': { opacity: 0.7 }
                                }}
                            >
                                <Badge
                                    color="primary"
                                    variant="dot"
                                    invisible={!customScanPrompt}
                                    sx={{ '& .MuiBadge-badge': { top: 4, right: 4 } }}
                                >
                                    <TuneIcon fontSize="small" />
                                </Badge>
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Each document page is processed separately. You can add multiple pages of the same statement.">
                            <IconButton size="small" sx={{ color: C.primary, '&:hover': { opacity: 0.7 } }}>
                                <HelpOutlineIcon />
                            </IconButton>
                        </Tooltip>
                        <IconButton onClick={handleClose} size="small" sx={{ color: C.primary, '&:hover': { opacity: 0.7 } }}>
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </Box>

                <DialogContent sx={{ px: 3, py: 0, overflowY: 'auto', flex: 1, minHeight: 0, pb: activeTxnsCount > 0 ? '140px' : 4 }}>
                    <Box sx={{ mt: 2 }}>
                        <QuotaBadge featureKey="ai_document_scan" />
                    </Box>

                    {parseError && (
                        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setParseError('')}>
                            {parseError}
                        </Alert>
                    )}

                    {/* Ingest Actions */}
                    {uploads.length === 0 && (
                        <Box component="section" sx={{ mb: 4, mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                            <Box
                                component="button"
                                disabled={isParsing}
                                onClick={() => setCameraOpen(true)}
                                sx={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5,
                                    py: 2,
                                    borderRadius: '8px',
                                    border: 'none',
                                    cursor: isParsing ? 'not-allowed' : 'pointer',
                                    bgcolor: C.primary,
                                    color: C.onPrimary,
                                    fontFamily: font.headline, fontWeight: 700, fontSize: '1rem',
                                    boxShadow: (theme) => `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`,
                                    transition: 'all 0.2s ease',
                                    '&:hover': { bgcolor: 'primary.dark' },
                                    '&:active': { transform: 'scale(0.98)' },
                                    opacity: isParsing ? 0.6 : 1,
                                    }}
                            >
                                <PhotoCameraIcon />
                                {t('Scan with Camera')}
                            </Box>

                            <Box
                                component="button"
                                disabled={isParsing}
                                onClick={() => fileInputRef.current?.click()}
                                sx={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5,
                                    py: 2,
                                    borderRadius: '8px',
                                    border: (theme) => `1px solid ${theme.palette.text.secondary}`,
                                    cursor: isParsing ? 'not-allowed' : 'pointer',
                                    bgcolor: 'transparent',
                                    color: 'text.secondary',
                                    fontFamily: font.headline, fontWeight: 700, fontSize: '1rem',
                                    transition: 'all 0.2s ease',
                                    '&:hover': { bgcolor: (theme) => alpha(theme.palette.text.secondary, 0.05) },
                                    '&:active': { transform: 'scale(0.98)' },
                                    opacity: isParsing ? 0.6 : 1,
                                }}
                            >
                                <UploadFileIcon />
                                {t('Upload from Files')}
                            </Box>
                        </Box>
                    )}
                    <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        accept="image/*,application/pdf,.csv,.xlsx,.xls,.ofx,.qfx,.qbo,.qif,.iif"
                        multiple
                        onChange={handleFileChange}
                    />

                    {/* Section 3: Current Batch */}
                    {uploads.length > 0 && (
                        <Box
                            component="section"
                            sx={{
                                bgcolor: C.surfaceCont,
                                borderRadius: '1rem',
                                p: 3,
                                mb: 4,
                                position: 'relative',
                                overflow: 'hidden',
                            }}
                        >
                            {/* Texture blob */}
                            <Box sx={{ position: 'absolute', right: -40, bottom: -40, width: 120, height: 120, bgcolor: `${C.primary}0D`, borderRadius: '50%', filter: 'blur(32px)', pointerEvents: 'none' }} />

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                                <Box>
                                    <Typography sx={{ fontFamily: font.headline, fontWeight: 700, fontSize: '1.125rem', lineHeight: 1.2 }}>
                                        {t('Current Batch')}
                                    </Typography>
                                    <Typography sx={{ fontFamily: font.body, fontSize: '0.65rem', fontWeight: 600, color: C.onSurfaceVar, letterSpacing: '0.15em', textTransform: 'uppercase', mt: 0.5 }}>
                                        {uploads.length} {uploads.length > 1 ? 'pages' : 'page'}
                                    </Typography>
                                </Box>
                                <Box sx={{ bgcolor: C.surfaceContHighest, px: 1.5, py: 0.5, borderRadius: '999px' }}>
                                    <Typography sx={{ fontFamily: font.body, fontSize: '0.6rem', fontWeight: 700, color: C.primary, letterSpacing: '0.1em' }}>
                                        {isParsing ? 'ANALYZING' : 'DRAFT'}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Thumbnail reel */}
                            <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1, scrollSnapType: 'x mandatory', '&::-webkit-scrollbar': { display: 'none' } }}>
                                {uploads.map((u, i) => (
                                    <Box key={u.previewUrl || i} sx={{ scrollSnapAlign: 'start' }}>
                                        <ThumbnailItem
                                            upload={u}
                                            index={i}
                                            isActive={u.status === 'uploading'}
                                            isRemoving={removingUploadIndices.has(i)}
                                            onClick={() => setPreviewUpload(u)}
                                            onDelete={() => removeUpload(i)}
                                        />
                                    </Box>
                                ))}
                                {/* Add next page slot */}
                                <Box
                                    component="button"
                                    onClick={() => setCameraOpen(true)}
                                    disabled={isParsing}
                                    sx={{
                                        flexShrink: 0, width: 96,
                                        aspectRatio: '3/4',
                                        border: `2px dashed ${C.outlineVar}4D`,
                                        borderRadius: '0.5rem',
                                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                        bgcolor: 'transparent',
                                        cursor: isParsing ? 'not-allowed' : 'pointer',
                                        transition: 'background-color 0.2s ease',
                                        '&:hover': !isParsing ? { bgcolor: C.surfaceContHigh } : {},
                                        scrollSnapAlign: 'start',
                                    }}
                                >
                                    <PhotoCameraIcon sx={{ color: `${C.onSurfaceVar}66`, fontSize: 24, mb: 0.5 }} />
                                    <Typography sx={{ fontFamily: font.body, fontSize: '0.55rem', fontWeight: 700, color: `${C.onSurfaceVar}99`, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                                        {t('Scan Page')}
                                    </Typography>
                                </Box>
                                <Box
                                    component="button"
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={isParsing}
                                    sx={{
                                        flexShrink: 0, width: 96,
                                        aspectRatio: '3/4',
                                        border: `2px dashed ${C.outlineVar}4D`,
                                        borderRadius: '0.5rem',
                                        display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                        bgcolor: 'transparent',
                                        cursor: isParsing ? 'not-allowed' : 'pointer',
                                        transition: 'background-color 0.2s ease',
                                        '&:hover': !isParsing ? { bgcolor: C.surfaceContHigh } : {},
                                        scrollSnapAlign: 'start',
                                    }}
                                >
                                    <UploadFileIcon sx={{ color: `${C.onSurfaceVar}66`, fontSize: 24, mb: 0.5 }} />
                                    <Typography sx={{ fontFamily: font.body, fontSize: '0.55rem', fontWeight: 700, color: `${C.onSurfaceVar}99`, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                                        {t('Upload Page')}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Progress bar while parsing */}
                            {isParsing && (
                                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <CircularProgress size={14} sx={{ color: C.primary }} />
                                    <Typography sx={{ fontFamily: font.body, fontSize: '0.7rem', color: C.onSurfaceVar }}>
                                        {t('Extracting transactions…')}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Section 4: Transaction Review */}
                    {reviewRows.length > 0 && (
                        <Box component="section" sx={{ mb: 4 }}>
                            {dupCount > 0 && (
                                <Alert
                                    severity="warning"
                                    icon={<WarningAmberIcon sx={{ fontSize: 16 }} />}
                                    sx={{ mb: 2.5, borderRadius: '0.75rem', fontFamily: font.body }}
                                >
                                    {dupCount} transaction{dupCount > 1 ? 's' : ''} may already exist in your ledger.
                                </Alert>
                            )}
                            {batchDupCount > 0 && (
                                <Alert
                                    severity="warning"
                                    icon={<WarningAmberIcon sx={{ fontSize: 16 }} />}
                                    sx={{ mb: 2.5, borderRadius: '0.75rem', fontFamily: font.body }}
                                >
                                    {batchDupCount} duplicate transaction{batchDupCount > 1 ? 's' : ''} detected within this batch.
                                </Alert>
                            )}
                            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                {reviewRows.map((row, i) => {
                                    const rowKey = row._rowKey || `row_${i}`;
                                    const isRemoving = removingRowKeys.has(rowKey);
                                    if (row.isEmptyPlaceholder) {
                                        return (
                                            <EmptyPagePlaceholder
                                                key={rowKey}
                                                uploadIdx={row.uploadIdx}
                                            />
                                        );
                                    }
                                    return (
                                        <Collapse
                                            key={rowKey}
                                            in={!isRemoving}
                                            timeout={AnimationTiming.RECORD_REMOVE_MS}
                                            unmountOnExit={false}
                                        >
                                            <Box sx={{ mb: 2.5 }}>
                                                <TransactionCard
                                                    row={row}
                                                    index={i}
                                                    isRemoving={isRemoving}
                                                    categories={categories}
                                                    creditCards={creditCards}
                                                    accounts={accounts}
                                                    onUpdate={updateRow}
                                                    onRemove={removeRow}
                                                    isParsing={isParsing}
                                                    isBatchDuplicate={batchDuplicates.has(i)}
                                                />
                                            </Box>
                                        </Collapse>
                                    );
                                })}
                            </Box>
                        </Box>
                    )}
                </DialogContent>

                {/* ── Fixed Bottom CTA ── */}
                {activeTxnsCount > 0 && (
                    <Box sx={{
                        position: 'absolute', bottom: 0, left: 0, right: 0,
                        bgcolor: C.surfaceContLow,
                        px: 3, pt: 2.5, pb: 3,
                        boxShadow: '0 -4px 24px rgba(28,28,24,0.08)',
                        borderTop: `1px solid ${C.outlineVar}1A`,
                        zIndex: 10,
                    }}>
                        <Box
                            component="button"
                            onClick={handleSave}
                            disabled={isSaving || isParsing}
                            sx={{
                                width: '100%', height: 56,
                                borderRadius: '8px',
                                border: 'none',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5,
                                cursor: (isSaving || isParsing) ? 'not-allowed' : 'pointer',
                                bgcolor: (isSaving || isParsing)
                                    ? C.surfaceContHighest
                                    : C.primary,
                                color: C.onPrimary,
                                transition: 'all 0.2s ease',
                                boxShadow: !isSaving ? (theme) => `0 6px 24px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
                                '&:hover': {
                                    bgcolor: (isSaving || isParsing)
                                        ? C.surfaceContHighest
                                        : 'primary.dark'
                                },
                                '&:active': { transform: 'scale(0.98)' },
                            }}
                        >
                            {isSaving ? (
                                <CircularProgress size={20} sx={{ color: '#fff' }} />
                            ) : (
                                <VerifiedIcon sx={{ color: C.onPrimary, fontSize: 22 }} />
                            )}
                            <Typography sx={{
                                fontFamily: font.headline, fontWeight: 700,
                                fontSize: '0.875rem', letterSpacing: '0.05em',
                                color: C.onPrimary,
                            }}>
                                {isSaving
                                    ? 'Saving…'
                                    : `${t('Save')} ${activeTxnsCount} ${t('Transactions')}`}
                            </Typography>
                        </Box>
                        <Typography sx={{
                            textAlign: 'center', mt: 1.5,
                            fontFamily: font.body, fontSize: '0.6rem',
                            color: `${C.onSurfaceVar}99`,
                            letterSpacing: '0.05em', textTransform: 'uppercase',
                        }}>
                            By importing, you confirm all data matches your records
                        </Typography>
                    </Box>
                )}
            </Dialog>

            {/* Zoom / Lightbox Dialog for Scan Document Uploads */}
            <Dialog
                open={Boolean(previewUpload)}
                onClose={() => setPreviewUpload(null)}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: {
                        bgcolor: 'rgba(20, 20, 20, 0.95)',
                        boxShadow: 'none',
                        borderRadius: 4,
                        overflow: 'hidden'
                    }
                }}
            >
                {previewUpload && (
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#fff', borderBottom: '1px solid rgba(255,255,255,0.1)', px: 3, py: 1.5 }}>
                            <Typography variant="subtitle1" sx={{ fontFamily: font.headline, fontWeight: 600 }}>
                                Document Preview - {previewUpload.file.name}
                            </Typography>
                            <IconButton onClick={() => setPreviewUpload(null)} sx={{ color: '#fff' }}>
                                &times;
                            </IconButton>
                        </Box>
                        <DialogContent sx={{ p: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh', overflow: 'hidden', bgcolor: '#121212' }}>
                            {previewUpload.file.type?.startsWith('image/') ? (
                                <img
                                    src={previewUpload.previewUrl}
                                    alt="Zoomed Document"
                                    style={{ maxWidth: '100%', maxHeight: '80vh', objectFit: 'contain', borderRadius: 8 }}
                                />
                            ) : (
                                <iframe
                                    src={previewUpload.previewUrl}
                                    title="PDF Document Preview"
                                    width="100%"
                                    height="600px"
                                    style={{ border: 'none', borderRadius: 8, backgroundColor: '#fff' }}
                                />
                            )}
                        </DialogContent>
                    </>
                )}
            </Dialog>

            {/* ── AI Prompt Settings Dialog ── */}
            <Dialog
                open={settingsOpen}
                onClose={() => {
                    setPromptInput(customScanPrompt);
                    setSettingsOpen(false);
                }}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        p: 1,
                    }
                }}
            >
                <DialogTitle sx={{ fontFamily: font.headline, fontWeight: 700, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TuneIcon sx={{ color: C.primary }} />
                        <span>{t('AI Description Prompt')}</span>
                    </Box>
                    <IconButton
                        size="small"
                        onClick={() => {
                            setPromptInput(customScanPrompt);
                            setSettingsOpen(false);
                        }}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Typography variant="body2" sx={{ color: C.onSurfaceVar, mb: 2, fontFamily: font.body }}>
                        {t('Instruct the AI model how to clean and format transaction descriptions when scanning documents and receipts for this ledger.')}
                    </Typography>

                    <TextField
                        fullWidth
                        multiline
                        rows={3}
                        value={promptInput}
                        onChange={(e) => setPromptInput(e.target.value)}
                        placeholder={t('e.g., Remove meaningless IDs, do not show INTERAC transfer, format merchant names cleanly')}
                        inputProps={{ maxLength: 500 }}
                        helperText={`${promptInput.length} / 500`}
                        FormHelperTextProps={{ sx: { textAlign: 'right' } }}
                        sx={{
                            mb: 2,
                            '& .MuiOutlinedInput-root': {
                                borderRadius: '12px',
                            }
                        }}
                    />

                    <Typography variant="caption" sx={{ fontWeight: 600, color: C.onSurfaceVar, display: 'block', mb: 1 }}>
                        {t('Quick Presets')}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                        {PROMPT_PRESETS.map((preset, idx) => (
                            <Chip
                                key={idx}
                                label={preset.label}
                                size="small"
                                variant="outlined"
                                onClick={() => {
                                    setPromptInput(prev => {
                                        const trimmed = prev.trim();
                                        if (!trimmed) return preset.text;
                                        if (trimmed.includes(preset.text)) return trimmed;
                                        const combined = `${trimmed}, ${preset.text}`;
                                        return combined.length > 500 ? combined.substring(0, 500) : combined;
                                    });
                                }}
                                sx={{ cursor: 'pointer', mb: 0.5, borderRadius: '8px' }}
                            />
                        ))}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
                    <Button
                        color="error"
                        onClick={() => setPromptInput('')}
                        disabled={!promptInput && !customScanPrompt}
                    >
                        {t('Clear')}
                    </Button>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                            variant="outlined"
                            onClick={() => {
                                setPromptInput(customScanPrompt);
                                setSettingsOpen(false);
                            }}
                            sx={{ borderRadius: '8px' }}
                        >
                            {t('Cancel')}
                        </Button>
                        <Button
                            variant="contained"
                            disabled={isSavingPrompt || promptInput.trim() === customScanPrompt}
                            onClick={handleSavePrompt}
                            sx={{ borderRadius: '8px' }}
                        >
                            {isSavingPrompt ? <CircularProgress size={20} color="inherit" /> : t('Save')}
                        </Button>
                    </Box>
                </DialogActions>
            </Dialog>

            {/* Camera scanner dialog */}
            <ReceiptCameraDialog
                open={cameraOpen}
                onClose={() => setCameraOpen(false)}
                api={api}
                ledgerId={ledgerId}
                onParsed={(data, blob) => {
                    handleCameraParsed(data, blob);
                }}
            />
        </>
    );
}
